import { FC, FormEvent, Fragment, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SmsFailedRoundedIcon from "@mui/icons-material/SmsFailedRounded";
import { ContainedButton } from "../../styled/contained-button";

const rowStyles: React.CSSProperties = {
  textAlign: "center",
  padding: "10px",
  fontWeight: "bold",
};

type IPendingAction = "backup" | "download";

interface IBackup {
  date: string;
  handleBackupAction: (password: string) => void;
  handleDownloadAction: (password: string) => void;
  status: boolean;
  loading: boolean;
}

export const DashboardBackup: FC<IBackup> = ({
  date,
  handleBackupAction,
  handleDownloadAction,
  status,
  loading,
}) => {
  const [pendingAction, setPendingAction] = useState<IPendingAction>();
  const [password, setPassword] = useState("");

  const closePasswordDialog = () => {
    setPendingAction(undefined);
    setPassword("");
  };

  const handleConfirm = (event: FormEvent) => {
    event.preventDefault();
    if (pendingAction === "backup") handleBackupAction(password);
    if (pendingAction === "download") handleDownloadAction(password);
    closePasswordDialog();
  };

  return (
    <Fragment>
      <Typography variant="h6" mt={5} mb={3}>
        Backup
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Das Backup läuft im Hintergrund und dauert etwa 5 Minuten. Bitte warten
        Sie, bis der Status aktualisiert ist, und starten Sie es nicht erneut.
        Der Download-Link ist danach 15 Minuten gültig.
      </Alert>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={rowStyles}>Vorname</TableCell>
            <TableCell style={rowStyles}>Date</TableCell>
            <TableCell style={rowStyles}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Grid
                  container
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={50} />
                </Grid>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell align="center">Backup</TableCell>
              <TableCell align="center">{date}</TableCell>
              <TableCell align="center">
                <Grid container justifyContent="center" alignContent="center">
                  {status ? (
                    <CheckCircleRoundedIcon
                      style={{ color: "green", marginRight: "10px" }}
                    />
                  ) : (
                    <SmsFailedRoundedIcon
                      style={{ color: "red", marginRight: "10px" }}
                    />
                  )}
                  <Typography>{status ? "Success" : "Failed"}</Typography>
                </Grid>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Grid container justifyContent="center" alignContent="center" gap={2}>
        <Grid item mt={5}>
          <ContainedButton
            onClick={() => setPendingAction("backup")}
            disabled={loading}
          >
            Backup
          </ContainedButton>
        </Grid>
        <Grid item mt={5}>
          <ContainedButton
            onClick={() => setPendingAction("download")}
            disabled={loading || !status}
          >
            Herunterladen
          </ContainedButton>
        </Grid>
      </Grid>

      <Dialog open={Boolean(pendingAction)} onClose={closePasswordDialog}>
        <form onSubmit={handleConfirm}>
          <DialogTitle>Backup-Passwort</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bitte geben Sie das Backup-Passwort ein.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              required
              margin="normal"
              type="password"
              label="Passwort"
              autoComplete="off"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closePasswordDialog}>Abbrechen</Button>
            <Button type="submit" variant="contained">
              Bestätigen
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
